/**
 * PackPaginationBar.jsx — RC 1.47.P0.2B Slice B
 *
 * Pagination strip for the Applications list. Mirrors the visual
 * grammar of `JobsPage`'s pagination control so the two list pages
 * feel like a single design system.
 *
 * Renders nothing when both `hasPrev` and `hasNext` are false (the
 * single-page case) — keeps the surface clean for the operator's
 * 14-pack baseline. The "Showing X-Y of Z" range works equally for
 * full and last-page slices.
 */

import PropTypes from 'prop-types';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function PackPaginationBar({
  page, pageSize, total, hasPrev, hasNext, onPageChange,
}) {
  if (!hasPrev && !hasNext) return null;
  const pageStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd   = Math.min(total, page * pageSize);

  return (
    <div
      className="flex items-center justify-between pt-2"
      data-testid="packs-pagination"
    >
      <p
        className="text-xs text-ndor-ink-muted font-mono-ui"
        data-testid="packs-page-state"
      >
        Showing <span className="tabular-nums">{pageStart.toLocaleString()}</span>–
        <span className="tabular-nums">{pageEnd.toLocaleString()}</span>
        {' '}of <span className="tabular-nums">{total.toLocaleString()}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={!hasPrev}
          data-testid="packs-page-prev"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Previous
        </Button>
        <span
          className="text-xs text-ndor-ink-muted font-mono-ui"
          data-testid="packs-page-indicator"
        >
          Page {page}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          data-testid="packs-page-next"
        >
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

PackPaginationBar.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  hasPrev: PropTypes.bool.isRequired,
  hasNext: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
};
